import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from '../../_interfaces/subject';
import { Visit } from '../../_interfaces/visit';
import { ManualPatientSelectorComponent } from '../manual-patient-selector/manual-patient-selector.component';
import { PatientSelectorComponent, PatientSource } from '../../shared/components/patient-selector/patient-selector.component';
import { TranslateModule } from '@ngx-translate/core';
import { EncounterService } from '../../_services/encounter.service';
import { FeatureFlagsService } from '../../_services/feature-flags.service';
import { canSelectVisit, continueWithOptionalVisit } from '../../_helpers/visit-selection';
import { APP_DIALOG_DATA } from '../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../shared/components/dialog/dialog-ref';
import { DialogService } from '../../shared/components/dialog/dialog.service';
import { DIALOG_IMPORTS } from '../../shared/components/dialog/dialog.imports';
import { PatientSelectEntryPoint } from '../../_services/analytics.service';

@Component({
  selector: 'app-start-encounter',
  standalone: true,
  imports: [CommonModule, FormsModule, ...DIALOG_IMPORTS, MatIconModule, MatButtonModule, PatientSelectorComponent, TranslateModule],
  templateUrl: './start-encounter.component.html'
})
export class StartEncounterComponent {
  headerLabelKey: string;
  selectedPatient: Subject | null = null;
  selectedPatientSource: PatientSource | null = null;
  isSubmitting = false;

  constructor(
    @Inject(APP_DIALOG_DATA) public data: { mode: 'consultation' | 'dictation' },
    private dialogRef: AppDialogRef<StartEncounterComponent>,
    private dialog: DialogService,
    private encounterService: EncounterService,
    private flags: FeatureFlagsService
  ) {
    this.headerLabelKey = data.mode === 'dictation' ? 'Start a dictation' : 'Start a consultation';
  }

  get isVisitSelectionEnabled(): boolean {
    return this.flags.isEnabled('visitSelection');
  }

  selectPatient(patient: Subject | null): void {
    this.selectedPatient = patient;
    if (!patient) return;
    // In the visit flow the choice is confirmed with Continue; otherwise selecting starts straight away.
    if (this.isVisitSelectionEnabled) return;
    this.createAndUpdate(patient);
  }

  selectPatientSource(source: PatientSource | null): void {
    this.selectedPatientSource = source;
  }

  onContinue(): void {
    if (!this.selectedPatient) return;
    continueWithOptionalVisit({
      dialog: this.dialog,
      dialogRef: this.dialogRef,
      patient: this.selectedPatient,
      mode: this.data.mode,
      flow: 'start-encounter',
      source: this.selectedPatientSource,
      isVisitSelectionEnabled: this.isVisitSelectionEnabled,
      onCommit: (visit) => this.createAndUpdate(this.selectedPatient!, visit),
    });
  }

  openAddPatient(): void {
    const ref = this.dialog.open(ManualPatientSelectorComponent, {
      size: 'sm',
      data: { from: 'consultation', mode: this.data.mode }
    });
    ref.beforeClosed().subscribe((res) => {
      if (res === 'close') {
        this.dialog.closeWithoutAnimation(this.dialogRef, 'close');
      }
    });
    ref.afterClosed().subscribe((res) => {
      if (!res || res === 'back') {
        this.dialog.show(this.dialogRef);
      }
    });
  }

  async createAndUpdate(patient?: Subject, visit?: Visit | null): Promise<void> {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    if (canSelectVisit(this.isVisitSelectionEnabled, this.selectedPatientSource, this.selectedPatient)) {
      this.dialog.closeWithoutAnimation(this.dialogRef);
    } else {
      this.dialogRef.close();
    }
    let entryPoint: PatientSelectEntryPoint = this.selectedPatientSource === 'search' ? 'all_patient' : 'schedule_patient_select';
    entryPoint = patient === undefined ? 'no_patient' : entryPoint;
    await this.encounterService.createAndNavigate(patient || null, this.data.mode, {
      visit: visit ?? null,
      entryPoint: this.data.mode === 'consultation' ? entryPoint : undefined,
    });
  }
}
